<template>
    <v-container class="chc-page">
        <ModuleClosedAlert module="site.chc.request" />
        <div class="limiter-content pa-2 pa-lg-0">
            <div class="chc-breadcrumb mt-6 mb-3">Início <span>›</span> Certidão de Habilitação de Crédito</div>

            <div class="chc-page-header">
                <div>
                    <h1 class="title-page mb-2" tabindex="0">
                        {{ dataset.mode === 'success'
                            ? 'Solicitação enviada'
                            : dataset.mode === 'form' && !dataset.readonly
                                ? 'Nova solicitação'
                                : 'Certidão de Habilitação de Crédito' }}
                    </h1>
                    <p class="chc-subtitle mb-0">
                        {{ dataset.mode === 'success'
                            ? 'Sua solicitação de Certidão de Habilitação de Crédito foi recebida.'
                            : dataset.mode === 'form' && !dataset.readonly
                                ? 'Cinco etapas curtas para enviar sua solicitação com todos os dados necessários.'
                                : 'Solicite e acompanhe a Certidão de Habilitação de Crédito (CHC) do seu crédito reconhecido em processo judicial.' }}
                    </p>
                </div>
                <v-btn v-if="user && !dataset.loading && dataset.mode === 'list' && dataset.records.length === 0"
                    color="primary" class="chc-primary-action" @click="createNew">
                    <v-icon left>mdi-plus</v-icon>Nova solicitação
                </v-btn>
            </div>

            <div class="mt-4 text-center" v-if="dataset.loading">
                <v-progress-circular indeterminate color="primary" :size="50"></v-progress-circular>
            </div>

            <template v-if="user && !dataset.loading && dataset.mode === 'list'">
                <section class="chc-intro-card">
                    <div class="chc-intro-copy">
                        <span class="chc-eyebrow">Antes de começar</span>
                        <p>
                            A CHC comprova o crédito reconhecido em processo judicial. Reúna os dados do processo
                            e os documentos necessários antes de iniciar.
                        </p>
                    </div>
                </section>

                <section class="chc-requirements">
                    <h2>O que você vai precisar</h2>
                    <div class="chc-requirements-grid">
                        <article v-for="item in requirementItems" :key="item.number" class="chc-requirement">
                            <span>{{ item.number }}</span>
                            <div>
                                <strong>{{ item.title }}</strong>
                                <p>{{ item.description }}</p>
                            </div>
                        </article>
                    </div>
                </section>

                <section v-if="pendingDocsRecords.length || outdatedRecords.length" class="chc-attention-list">
                    <article v-for="record in pendingDocsRecords" :key="`pending-${record.id}`" class="chc-attention-card">
                        <div class="chc-attention-icon"><v-icon color="#9A5A00">mdi-alert-circle-outline</v-icon></div>
                        <div class="chc-attention-content">
                            <strong>Envie os documentos solicitados</strong>
                            <p>A equipe pediu novos arquivos para concluir a análise da solicitação {{ record.protocol || record.uuid }}.</p>
                        </div>
                        <v-btn outlined color="primary" @click="openUpdateDialogFromRecord(record)">
                            Enviar documentos
                        </v-btn>
                    </article>
                    <article v-for="record in outdatedRecords" :key="`outdated-${record.id}`" class="chc-attention-card">
                        <div class="chc-attention-icon"><v-icon color="#9A5A00">mdi-alert-circle-outline</v-icon></div>
                        <div class="chc-attention-content">
                            <strong>Envie a certidão atualizada</strong>
                            <p>A certidão perdeu validade e precisa ser reenviada para continuidade da análise.</p>
                        </div>
                        <v-btn outlined color="primary"
                            @click="openOutdatedUpdateDialog(record)">
                            Enviar certidão atualizada
                        </v-btn>
                    </article>
                </section>

                <section v-if="dataset.records.length > 0" class="chc-requests-section">
                    <div class="chc-section-header">
                        <div>
                            <span class="chc-eyebrow">Minhas solicitações</span>
                            <h2>Acompanhe seus pedidos</h2>
                        </div>
                        <v-btn color="primary" class="chc-primary-action" @click="createNew">
                            <v-icon left>mdi-plus</v-icon>Nova solicitação
                        </v-btn>
                    </div>

                    <div class="chc-request-list">
                        <article v-for="record in dataset.records" :key="`record-${record.id}`" class="chc-request-card">
                            <div class="chc-request-main">
                                <div class="chc-request-heading">
                                    <v-chip small :color="getStatusColor(record.status)" text-color="white">
                                        {{ getStatusLabel(record.status) }}
                                    </v-chip>
                                    <strong>{{ record.protocol || record.uuid }}</strong>
                                </div>
                                <h3>{{ record.origin_process || '-' }}<span v-if="record.judging_body"> · {{ record.judging_body }}</span></h3>
                                <p>{{ getStatusDescription(record.status) }}</p>
                            </div>
                            <div class="chc-request-meta">
                                <div><span>Processo de origem</span><strong>{{ record.origin_process || '-' }}</strong></div>
                                <div><span>Devedora</span><strong>{{ getRecordDebtor(record) }}</strong></div>
                                <div><span>Enviada em</span><strong>{{ formatDate(record.created_at) }}</strong></div>
                                <div><span>Última atualização</span><strong>{{ formatDate(record.updated_at || record.created_at) }}</strong></div>
                            </div>
                            <v-btn outlined color="primary" @click="viewRecord(record)">Ver solicitação</v-btn>
                        </article>
                    </div>
                </section>

                <section v-else class="chc-empty-state">
                    <v-icon size="48" color="primary">mdi-file-document-outline</v-icon>
                    <h2>Nenhuma solicitação enviada</h2>
                    <p>Quando você enviar uma solicitação, poderá acompanhar aqui todas as atualizações.</p>
                </section>
            </template>

            <section v-if="user && !dataset.loading && dataset.mode === 'success'" class="chc-success-card">
                <div class="chc-success-icon">
                    <v-icon color="white" size="42">mdi-check</v-icon>
                </div>
                <span class="chc-eyebrow">Solicitação concluída</span>
                <h2>CHC enviada com sucesso</h2>
                <p>
                    Sua solicitação foi recebida e seguirá para análise. Guarde o número abaixo para acompanhar
                    o andamento.
                </p>
                <div class="chc-protocol-box">
                    <span>Número do protocolo</span>
                    <strong>{{ dataset.completedProtocol || 'Protocolo em processamento' }}</strong>
                </div>
                <v-btn color="primary" class="chc-primary-action" @click="backToList">
                    Acompanhar solicitações
                    <v-icon right>mdi-arrow-right</v-icon>
                </v-btn>
            </section>

            <div v-if="user && !dataset.loading && dataset.mode === 'form'"
                class="d-flex align-center mb-4 chc-form-back">
                <v-btn text @click="backToList">
                    <v-icon left>mdi-arrow-left</v-icon> Voltar
                </v-btn>
                <v-spacer />
                <v-chip v-if="dataset.readonly" color="primary" outlined>Visualização</v-chip>
            </div>

            <v-form v-if="user && !dataset.loading && dataset.mode === 'form'" ref="form" v-model="dataset.validForm"
                class="pt-2 chc-form-flow">
                <div v-if="!dataset.readonly" class="chc-stepper mb-7">
                    <button v-for="step in formSteps" :key="step.number" type="button"
                        :class="{ active: dataset.formStep === step.number, complete: dataset.formStep > step.number }"
                        @click="goToStep(step.number)">
                        <span>{{ dataset.formStep > step.number ? '✓' : step.number }}</span>
                        <strong>{{ step.title }}</strong>
                    </button>
                </div>

                <v-row v-if="dataset.readonly && (dataset.data.protocol || dataset.data.uuid)">
                    <v-col cols="12" sm="8">
                        <v-text-field dense outlined hide-details="auto" label="Protocolo"
                            :value="dataset.data.protocol || dataset.data.uuid" readonly />
                    </v-col>
                    <v-col cols="12" sm="4" class="d-flex align-center">
                        <strong class="mr-2">Status:</strong>
                        <v-chip small :color="getStatusColor(dataset.data.status)" text-color="white">
                            {{ getStatusLabel(dataset.data.status) }}
                        </v-chip>
                    </v-col>
                </v-row>

                <!-- Etapa 6: manifestação do credor sobre a CHC da Recuperanda (e a da Recuperanda sobre a CHC do credor). -->
                <ManifestationSection v-if="dataset.readonly && dataset.data.manifestation && dataset.currentRecordId"
                    :manifestation="dataset.data.manifestation" module-key="site.chc.contradictory"
                    :endpoint="`/api/lawyer/credit-certificate-requests/${dataset.currentRecordId}/manifestation`" subject-label="esta CHC"
                    overline="Certidão de Habilitação de Crédito" question="Você concorda com a CHC apresentada pela Recuperanda?" lawyer class="mb-4" />

                <section ref="step1" v-show="dataset.readonly || dataset.formStep === 1" class="chc-form-section">
                    <div v-if="!dataset.readonly" class="chc-form-section-heading">
                        <h2>Dados do processo</h2>
                        <p>Informe o processo em que o crédito foi reconhecido. Esses dados serão conferidos pela equipe.</p>
                    </div>
                <v-row>
                    <v-col cols="12" sm="4">
                        <v-text-field dense outlined hide-details="auto" label="Processo de origem"
                            v-model="dataset.data.process_origin" v-mask="$masks.cnj"
                            :rules="[$rules.required, $rules.cnj]" :readonly="dataset.readonly" required />
                    </v-col>
                    <v-col cols="12" sm="2">
                        <v-autocomplete dense outlined hide-details="auto" label="UF do processo"
                            :items="dataset.statesList" v-model="dataset.data.process_uf" item-value="name"
                            item-text="name" :rules="[$rules.required]" :readonly="dataset.readonly"
                            :menu-props="dataset.readonly ? { value: false } : {}" required />
                    </v-col>
                    <v-col cols="12" sm="6">
                        <v-autocomplete dense outlined hide-details="auto" label="Ramo da Justiça"
                            :items="dataset.justiceBranchTypes" v-model="dataset.data.justice_branch"
                            item-value="id" item-text="name" :rules="[$rules.required]" :readonly="dataset.readonly"
                            :menu-props="dataset.readonly ? { value: false } : {}" required />
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12" sm="6">
                        <v-text-field dense outlined hide-details="auto" label="Órgão Julgador"
                            v-model="dataset.data.judging_body" :rules="[$rules.required]" :readonly="dataset.readonly"
                            hint="Informe a vara em que tramita o processo" persistent-hint required />
                    </v-col>
                    <v-col cols="12" sm="3">
                        <v-text-field dense outlined hide-details="auto" type="date"
                            label="Data do trânsito em julgado" v-model="dataset.data.final_judgment_date"
                            :rules="[$rules.required]" :readonly="dataset.readonly" required />
                    </v-col>
                    <v-col cols="12" sm="3">
                        <v-text-field dense outlined hide-details="auto" type="date"
                            label="Data de atualização dos cálculos" v-model="dataset.data.calculation_update_date"
                            :rules="[$rules.required]" :readonly="dataset.readonly" required />
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12">
                        <v-autocomplete dense outlined hide-details="auto" multiple chips small-chips :deletable-chips="!dataset.readonly"
                            label="Devedor/Réu" :items="dataset.clients" v-model="dataset.data.debtors" item-value="id"
                            item-text="name" :rules="[v => (v && v.length > 0) || 'Selecione ao menos um devedor']"
                            :readonly="dataset.readonly" :menu-props="dataset.readonly ? { value: false } : {}"
                            required />
                    </v-col>
                </v-row>
                </section>

                <section ref="step2" v-show="dataset.readonly || dataset.formStep === 2" class="chc-form-section">
                    <div v-if="!dataset.readonly" class="chc-form-section-heading">
                        <span class="chc-form-step-label">Etapa 2 de 5</span>
                        <h2>Credores e créditos</h2>
                        <p>Confira os dados dos credores e informe os valores reconhecidos no processo.</p>
                    </div>

                    <v-card flat class="chc-data-card mb-5">
                        <v-card-title class="chc-data-card-title">
                            <div>
                                <span>Credores</span>
                                <small>{{ dataset.data.creditors.length }} {{ dataset.data.creditors.length === 1 ? 'credor informado' : 'credores informados' }}</small>
                            </div>
                            <v-spacer />
                            <v-btn v-if="!dataset.readonly" @click="addCreditor" color="primary"
                                text x-small class="chc-add-link">
                                <v-icon left x-small>mdi-plus</v-icon>Adicionar
                            </v-btn>
                        </v-card-title>
                        <v-card-text>
                            <div v-for="(creditor, index) in dataset.data.creditors" :key="`creditor-${index}`"
                                class="chc-entry">
                                <span v-if="dataset.data.creditors.length > 1" class="chc-entry-label">Credor {{ index + 1 }}</span>
                                <v-row dense>
                                    <v-col cols="12" md="7">
                                        <v-text-field dense outlined hide-details="auto" label="Nome completo ou razão social"
                                            v-model="creditor.name" :rules="[$rules.required]"
                                            :readonly="dataset.readonly" required />
                                    </v-col>
                                    <v-col cols="12" md="5">
                                        <v-text-field dense outlined hide-details="auto" label="CPF/CNPJ"
                                            v-model="creditor.registration"
                                            v-mask="isCnpj(creditor.registration) ? $masks.cnpj : $masks.cpf"
                                            :rules="[$rules.required, isCnpj(creditor.registration) ? $rules.cnpj : $rules.cpf]"
                                            :readonly="dataset.readonly" required />
                                    </v-col>
                                </v-row>
                                <v-btn v-if="!dataset.readonly && dataset.data.creditors.length > 1"
                                    class="chc-entry-remove" icon color="red" title="Remover credor"
                                    @click="removeCreditor(index)">
                                    <v-icon>mdi-delete-outline</v-icon>
                                </v-btn>
                            </div>
                        </v-card-text>
                    </v-card>

                    <v-card flat class="chc-data-card">
                        <v-card-title class="chc-data-card-title">
                            <div>
                                <span>Créditos</span>
                                <small>{{ dataset.data.credits.length }} {{ dataset.data.credits.length === 1 ? 'crédito informado' : 'créditos informados' }}</small>
                            </div>
                            <v-spacer />
                            <v-btn v-if="!dataset.readonly" @click="addCredit" color="primary"
                                text x-small class="chc-add-link">
                                <v-icon left x-small>mdi-plus</v-icon>Adicionar
                            </v-btn>
                        </v-card-title>
                        <v-card-text>
                            <div v-for="(credit, index) in dataset.data.credits" :key="`credit-${index}`"
                                class="chc-entry">
                                <span v-if="dataset.data.credits.length > 1" class="chc-entry-label">Crédito {{ index + 1 }}</span>
                                <v-row dense>
                                    <v-col cols="12" class="chc-credit-owner">
                                        <v-select dense outlined hide-details="auto"
                                            label="Este crédito pertence a"
                                            :items="creditorRegistrationItems" v-model="credit.creditor_registration"
                                            item-text="text" item-value="value" :rules="[$rules.required]"
                                            :readonly="dataset.readonly"
                                            :menu-props="dataset.readonly ? { value: false } : {}"
                                            hint="Selecione o credor relacionado a este crédito." persistent-hint required />
                                    </v-col>
                                    <v-col cols="12" md="2">
                                        <v-select dense outlined hide-details="auto" label="Moeda"
                                            :items="dataset.currencyTypes" v-model="credit.currency" item-value="id"
                                            item-text="name" :rules="[$rules.required]" :readonly="dataset.readonly"
                                            :menu-props="dataset.readonly ? { value: false } : {}" required />
                                    </v-col>
                                    <v-col cols="12" md="3">
                                        <v-currency-field dense outlined hide-details="auto" label="Valor"
                                            v-model="credit.amount" :allowNegative="false" :rules="[$rules.required]"
                                            :readonly="dataset.readonly" required />
                                    </v-col>
                                    <v-col cols="12" md="3">
                                        <v-select dense outlined hide-details="auto" label="Origem do crédito"
                                            :items="dataset.creditOriginTypes" v-model="credit.credit_origin"
                                            item-value="id" item-text="name" :rules="[$rules.required]"
                                            :readonly="dataset.readonly"
                                            :menu-props="dataset.readonly ? { value: false } : {}" required />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-select dense outlined hide-details="auto" label="Classe"
                                            :items="dataset.creditorTypes" v-model="credit.creditor_class"
                                            item-value="id" item-text="name" :rules="[$rules.required]"
                                            :readonly="dataset.readonly"
                                            :menu-props="dataset.readonly ? { value: false } : {}" required />
                                    </v-col>
                                </v-row>
                                <v-btn v-if="!dataset.readonly && dataset.data.credits.length > 1"
                                    class="chc-entry-remove" icon color="red" title="Remover crédito"
                                    @click="removeCredit(index)">
                                    <v-icon>mdi-delete-outline</v-icon>
                                </v-btn>
                            </div>
                        </v-card-text>
                    </v-card>
                </section>
                <section ref="step3" v-show="dataset.readonly || dataset.formStep === 3" class="chc-form-section">
                    <div v-if="!dataset.readonly" class="chc-form-section-heading">
                        <h2>Outras verbas</h2>
                        <p>Informe honorários advocatícios ou periciais somente quando houver fixação no processo.</p>
                    </div>
                <v-card outlined class="mb-4">
                    <v-card-title class="card-group-title">Outras verbas</v-card-title>
                    <v-divider />
                    <v-card-text>
                        <v-row>
                            <v-col cols="12">
                                <strong>Houve fixação de honorários advocatícios?</strong>
                                <v-radio-group v-model="dataset.data.has_lawyer_fees" row class="mt-2"
                                    hide-details="auto" :rules="[requiredChoice]" :readonly="dataset.readonly">
                                    <v-radio label="Sim" :value="true" />
                                    <v-radio label="Não" :value="false" />
                                </v-radio-group>
                            </v-col>
                        </v-row>

                        <template v-if="dataset.data.has_lawyer_fees === true">
                            <div v-for="(lawyer, index) in dataset.data.lawyers" :key="`lawyer-${index}`">
                                <v-row>
                                    <v-col cols="12" sm="4">
                                        <v-text-field dense outlined hide-details="auto" label="Nome do advogado"
                                            v-model="lawyer.name" :rules="[$rules.required]" :readonly="dataset.readonly" required />
                                    </v-col>
                                    <v-col cols="12" sm="3">
                                        <v-text-field dense outlined hide-details="auto" label="CPF"
                                            v-model="lawyer.registration" v-mask="$masks.cpf"
                                            :rules="[$rules.required, $rules.cpf]" :readonly="dataset.readonly" required />
                                    </v-col>
                                    <v-col cols="12" sm="2">
                                        <v-text-field dense outlined hide-details="auto" label="OAB"
                                            v-model="lawyer.oab" :rules="[$rules.required]" :readonly="dataset.readonly" required />
                                    </v-col>
                                    <v-col cols="12" sm="2">
                                        <v-autocomplete dense outlined hide-details="auto" label="UF"
                                            :items="dataset.statesList" v-model="lawyer.state" item-value="name"
                                            item-text="name" :rules="[$rules.required]" :readonly="dataset.readonly"
                                            :menu-props="dataset.readonly ? { value: false } : {}" required />
                                    </v-col>
                                    <v-col cols="12" sm="1" class="d-flex align-center">
                                        <v-btn v-if="!dataset.readonly && dataset.data.lawyers.length > 1" icon color="red"
                                            @click="removeLawyer(index)">
                                            <v-icon>mdi-delete</v-icon>
                                        </v-btn>
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col cols="12" sm="3">
                                        <v-currency-field dense outlined hide-details="auto" label="Valor dos honorários"
                                            v-model="lawyer.amount" :allowNegative="false" :rules="[$rules.required]"
                                            :readonly="dataset.readonly" required />
                                    </v-col>
                                    <v-col cols="12" sm="3">
                                        <v-text-field dense outlined hide-details="auto" type="date"
                                            label="Data de fixação" v-model="lawyer.fixed_at"
                                            :rules="[$rules.required]" :readonly="dataset.readonly" required />
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <template v-if="dataset.readonly">
                                            <v-btn v-if="lawyer.document_file_url" outlined color="primary" @click="openDoc(lawyer.document_file_url)">
                                                <v-icon left>mdi-paperclip</v-icon>Baixar documento dos honorários
                                            </v-btn>
                                            <span v-else class="text--secondary">Nenhum documento anexado.</span>
                                        </template>
                                        <v-file-input v-else outlined dense prepend-icon="mdi-paperclip"
                                            accept="application/pdf,image/jpeg,image/png" truncate-length="70" show-size counter
                                            label="Sentença/documento dos honorários"
                                            hint="Anexe a sentença, decisão ou documento judicial que fixou os honorários"
                                            hide-details="auto" v-model="lawyer.document_file"
                                            :rules="[$rules.fileSize(20)]" />
                                    </v-col>
                                </v-row>
                            </div>
                            <v-row v-if="!dataset.readonly">
                                <v-col cols="12">
                                    <v-btn small outlined color="primary" @click="addLawyer">
                                        <v-icon left>mdi-plus</v-icon>Adicionar advogado
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </template>

                        <v-divider class="my-4" />

                        <v-row>
                            <v-col cols="12">
                                <strong>Houve fixação de honorários periciais?</strong>
                                <v-radio-group v-model="dataset.data.has_expert_fees" row class="mt-2"
                                    hide-details="auto" :rules="[requiredChoice]" :readonly="dataset.readonly">
                                    <v-radio label="Sim" :value="true" />
                                    <v-radio label="Não" :value="false" />
                                </v-radio-group>
                            </v-col>
                        </v-row>

                        <template v-if="dataset.data.has_expert_fees === true">
                            <div v-for="(expert, index) in dataset.data.experts" :key="`expert-${index}`">
                                <v-row>
                                    <v-col cols="12" sm="4">
                                        <v-text-field dense outlined hide-details="auto" label="Nome do perito"
                                            v-model="expert.name" :rules="[$rules.required]" :readonly="dataset.readonly" required />
                                    </v-col>
                                    <v-col cols="12" :sm="dataset.readonly ? 8 : 7">
                                        <v-text-field dense outlined hide-details="auto" label="CPF/CNPJ do perito"
                                            v-model="expert.registration"
                                            v-mask="isCnpj(expert.registration) ? $masks.cnpj : $masks.cpf"
                                            :rules="[$rules.required, isCnpj(expert.registration) ? $rules.cnpj : $rules.cpf]"
                                            :readonly="dataset.readonly" required />
                                    </v-col>
                                    <v-col v-if="!dataset.readonly" cols="12" sm="1" class="d-flex align-center">
                                        <v-btn v-if="dataset.data.experts.length > 1" icon color="red"
                                            @click="removeExpert(index)">
                                            <v-icon>mdi-delete</v-icon>
                                        </v-btn>
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col cols="12" sm="3">
                                        <v-currency-field dense outlined hide-details="auto" label="Valor dos honorários"
                                            v-model="expert.amount" :allowNegative="false" :rules="[$rules.required]"
                                            :readonly="dataset.readonly" required />
                                    </v-col>
                                    <v-col cols="12" sm="3">
                                        <v-text-field dense outlined hide-details="auto" type="date"
                                            label="Data de fixação" v-model="expert.fixed_at"
                                            :rules="[$rules.required]" :readonly="dataset.readonly" required />
                                    </v-col>
                                    <v-col cols="12" sm="6">
                                        <template v-if="dataset.readonly">
                                            <v-btn v-if="expert.document_file_url" outlined color="primary" @click="openDoc(expert.document_file_url)">
                                                <v-icon left>mdi-paperclip</v-icon>Baixar documento dos honorários
                                            </v-btn>
                                            <span v-else class="text--secondary">Nenhum documento anexado.</span>
                                        </template>
                                        <v-file-input v-else outlined dense prepend-icon="mdi-paperclip"
                                            accept="application/pdf,image/jpeg,image/png" truncate-length="70" show-size counter
                                            label="Sentença/documento dos honorários"
                                            hint="Anexe a sentença, decisão ou documento judicial que fixou os honorários"
                                            hide-details="auto" v-model="expert.document_file"
                                            :rules="[$rules.fileSize(20)]" />
                                    </v-col>
                                </v-row>
                            </div>
                            <v-row v-if="!dataset.readonly">
                                <v-col cols="12">
                                    <v-btn small outlined color="primary" @click="addExpert">
                                        <v-icon left>mdi-plus</v-icon>Adicionar perito
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </template>
                    </v-card-text>
                </v-card>

                </section>
                <section ref="step4" v-show="dataset.readonly || dataset.formStep === 4" class="chc-form-section">
                    <div v-if="!dataset.readonly" class="chc-form-section-heading">
                        <h2>Documentos</h2>
                        <p>Anexe a certidão e os demais documentos que apoiam a solicitação.</p>
                    </div>
                <v-row>
                    <v-col cols="12">
                        <strong>Incluir CHC</strong>
                        <template v-if="dataset.readonly">
                            <div class="mt-2">
                                <!-- Download da CHC temporariamente oculto.
                                <v-btn v-if="dataset.data.chc_file_url" outlined color="primary" @click="openDoc(dataset.data.chc_file_url)">
                                    <v-icon left>mdi-paperclip</v-icon>Baixar Certidão de Habilitação de Crédito
                                </v-btn>
                                -->
                                <span v-if="!dataset.data.chc_file_url" class="text--secondary">Nenhum arquivo anexado.</span>
                            </div>
                        </template>
                        <v-file-input v-else class="mt-2" outlined dense prepend-icon="mdi-paperclip"
                            accept="application/pdf" truncate-length="70" show-size counter
                            label="Anexar Certidão de Habilitação de Crédito"
                            hint="O arquivo não pode ser superior a 10MB" hide-details="auto"
                            v-model="dataset.data.chc_file"
                            :rules="[$rules.required, $rules.fileSize(10)]" required />
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12" sm="6">
                        <template v-if="dataset.readonly">
                            <div class="font-weight-bold mb-2">Decisão homologatória de cálculo</div>
                            <v-btn v-if="dataset.data.homologation_decision_file_url" outlined color="primary" @click="openDoc(dataset.data.homologation_decision_file_url)">
                                <v-icon left>mdi-paperclip</v-icon>Baixar decisão homologatória
                            </v-btn>
                            <span v-else class="text--secondary">Nenhum arquivo anexado.</span>
                        </template>
                        <v-file-input v-else outlined dense prepend-icon="mdi-paperclip"
                            accept="application/pdf,image/jpeg,image/png" truncate-length="70" show-size counter
                            label="Decisão homologatória de cálculo (opcional)"
                            hide-details="auto" v-model="dataset.data.homologation_decision_file"
                            :rules="[$rules.fileSize(20)]" />
                    </v-col>
                    <v-col cols="12" sm="6">
                        <template v-if="dataset.readonly">
                            <div class="font-weight-bold mb-2">Planilha de cálculos</div>
                            <v-btn v-if="dataset.data.calculation_spreadsheet_file_url" outlined color="primary" @click="openDoc(dataset.data.calculation_spreadsheet_file_url)">
                                <v-icon left>mdi-paperclip</v-icon>Baixar planilha de cálculos
                            </v-btn>
                            <span v-else class="text--secondary">Nenhum arquivo anexado.</span>
                        </template>
                        <v-file-input v-else outlined dense prepend-icon="mdi-paperclip"
                            accept=".pdf,.xls,.xlsx,.csv,image/jpeg,image/png" truncate-length="70" show-size counter
                            label="Planilha de cálculos (opcional)"
                            hide-details="auto" v-model="dataset.data.calculation_spreadsheet_file"
                            :rules="[$rules.fileSize(20)]" />
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12">
                        <strong>Processo sigiloso?</strong>
                        <v-radio-group v-model="dataset.data.is_confidential" row class="mt-2" hide-details="auto"
                            :rules="[requiredChoice]" :readonly="dataset.readonly">
                            <v-radio label="Sim" :value="true" />
                            <v-radio label="Não" :value="false" />
                        </v-radio-group>
                    </v-col>
                </v-row>

                <v-row v-if="dataset.data.is_confidential === true">
                    <v-col cols="12">
                        <template v-if="dataset.readonly">
                            <v-btn v-if="dataset.data.full_process_file_url" outlined color="primary" @click="openDoc(dataset.data.full_process_file_url)">
                                <v-icon left>mdi-paperclip</v-icon>Baixar processo integral
                            </v-btn>
                            <span v-else class="text--secondary">Nenhum arquivo anexado.</span>
                        </template>
                        <v-file-input v-else outlined dense prepend-icon="mdi-paperclip" accept="application/pdf"
                            truncate-length="70" show-size counter label="Processo integral (PDF único)"
                            hint="Anexe o processo integral em um único arquivo PDF" hide-details="auto"
                            v-model="dataset.data.full_process_file" :rules="[$rules.fileSize(50)]" />
                    </v-col>
                </v-row>

                </section>
                <section ref="step5" v-show="dataset.readonly || dataset.formStep === 5" class="chc-form-section">
                    <div v-if="!dataset.readonly" class="chc-form-section-heading">
                        <h2>Conferência e envio</h2>
                        <p>Revise as informações preenchidas e inclua uma observação, se necessário, antes de enviar.</p>
                    </div>
                <v-row>
                    <v-col cols="12">
                        <v-textarea outlined dense label="Observações" v-model="dataset.data.observations"
                            :counter="!dataset.readonly ? observationsMaxLength : false" :maxlength="observationsMaxLength" rows="4"
                            hide-details="auto" :readonly="dataset.readonly" />
                    </v-col>
                </v-row>

                </section>

                <div v-if="!dataset.readonly" class="chc-form-actions mt-5">
                    <v-btn text @click="backToList">Cancelar</v-btn>
                    <v-spacer />
                    <v-btn v-if="dataset.formStep > 1" outlined color="primary" @click="dataset.formStep--">
                        <v-icon left>mdi-arrow-left</v-icon>Voltar
                    </v-btn>
                    <v-btn v-if="dataset.formStep < 5" color="primary" @click="nextStep">
                        Continuar<v-icon right>mdi-arrow-right</v-icon>
                    </v-btn>
                    <v-btn v-else color="primary" :disabled="dataset.loadingSave" @click="onSubmitClick">
                        Finalizar
                        <v-progress-circular v-if="dataset.loadingSave" indeterminate color="white" size="20"
                            class="ml-2" />
                    </v-btn>
                </div>
            </v-form>

            <div class='mt-10' v-if='!user'>
                <h4>Por favor, realize o seu login para ter acesso à Análise de Ofício.</h4>
            </div>

        </div>

        <v-dialog v-model="dataset.updateDialog" max-width="700px" persistent>
            <v-card>
                <v-card-title class="card-group-title">Atualização de documentos solicitados</v-card-title>
                <v-divider />
                <v-card-text class="pt-4">
                    <p>
                        Solicitação realizada em
                        <strong>{{ formatDate(dataset.requestedDocsAt) }}</strong>.
                    </p>
                    <p>Foram solicitados os seguintes documentos. Anexe os arquivos atualizados abaixo:</p>
                    <v-form ref="updateForm" v-model="dataset.validUpdateForm">
                        <div v-for="file in dataset.requestedFiles" :key="`req-file-${file.type}`" class="mb-4">
                            <strong>{{ getDocTypeLabel(file.type) }}</strong>
                            <v-file-input class="mt-2" outlined dense prepend-icon="mdi-paperclip"
                                accept="application/pdf" truncate-length="70" show-size counter
                                :label="`Anexar ${getDocTypeLabel(file.type)}`"
                                :hint="`O arquivo não pode ser superior a ${file.type === 2 ? 50 : 10}MB`"
                                hide-details="auto"
                                :value="dataset.updateFiles[file.type]"
                                @change="onUpdateFileChange(file.type, $event)"
                                :rules="[$rules.required, $rules.fileSize(file.type === 2 ? 50 : 10)]" required />
                        </div>
                    </v-form>
                </v-card-text>
                <v-card-actions class="pb-4 px-4">
                    <v-spacer />
                    <v-btn text @click="dataset.updateDialog = false" :disabled="dataset.loadingUpdate">Cancelar</v-btn>
                    <v-btn color="primary" :disabled="dataset.loadingUpdate" @click="submitDocumentUpdate">
                        Enviar
                        <v-progress-circular v-if="dataset.loadingUpdate" indeterminate color="white" size="20" class="ml-2" />
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="dataset.confirmDialog" max-width="600px" persistent>
            <v-card>
                <v-card-title class="card-group-title">Atenção!</v-card-title>
                <v-divider />
                <v-card-text class="pt-4">
                    Caso a Certidão de Habilitação de Crédito tenha por origem processo sigiloso, a ausência de envio
                    da cópia integral poderá impossibilitar a alteração do crédito na relação de credores. Podemos
                    prosseguir?
                </v-card-text>
                <v-card-actions class="pb-4 px-4">
                    <v-spacer />
                    <v-btn text @click="dataset.confirmDialog = false">Não, desejo alterar as informações</v-btn>
                    <v-btn color="primary" :disabled="dataset.loadingSave" @click="confirmSubmit">Sim</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

    </v-container>

</template>

<script>
import ManifestationSection from '@/components/manifestation/ManifestationSection.vue'
import moment from 'moment';
import 'moment/locale/pt-br';
import axios from 'axios';

import { eventHub } from "@/main";
import currencyTypes from '@/common/data/currency-types.data';
import statesList from '@/common/data/states-list.data';
import creditorTypes from '@/common/data/creditor-types.data';
import justiceBranchTypes from '@/common/data/justice-branch-types.data';
import creditOriginTypes from '@/common/data/credit-certificate-credit-origin-types.data';
import certificateStatus from '@/common/data/credit-certificate-status.data';

const OBSERVATIONS_MAX_LENGTH = 1000;

export default {
    components: { ManifestationSection },

    data: () => ({
        user: null,
        observationsMaxLength: OBSERVATIONS_MAX_LENGTH,
        requirementItems: [
            { number: 1, title: 'Certidão de Habilitação de Crédito', description: 'Emitida pelo juízo, em PDF, com data posterior à atualização dos cálculos.' },
            { number: 2, title: 'Dados do processo de origem', description: 'Número, vara, ramo da Justiça e data do trânsito em julgado.' },
            { number: 3, title: 'Valores por credor', description: 'Valor, moeda, origem e classe de cada crédito.' },
            { number: 4, title: 'Processo integral, se sigiloso', description: 'PDF completo, tratado como documento restrito.' }
        ],
        formSteps: [
            { number: 1, title: 'Dados do processo' },
            { number: 2, title: 'Credores e créditos' },
            { number: 3, title: 'Outras verbas' },
            { number: 4, title: 'Documentos' },
            { number: 5, title: 'Conferência e envio' }
        ],
        dataset: {
            description: '',
            loading: false,
            loadingSave: false,
            validForm: false,
            confirmDialog: false,
            updateDialog: false,
            loadingUpdate: false,
            validUpdateForm: false,
            currentRecordId: null,
            requestedFiles: [],
            requestedDocsAt: null,
            updateFiles: {},
            mode: 'list',
            formStep: 1,
            completedProtocol: null,
            readonly: false,
            records: [],
            currencyTypes: currencyTypes,
            statesList: statesList,
            creditorTypes: creditorTypes,
            justiceBranchTypes: justiceBranchTypes,
            creditOriginTypes: creditOriginTypes,
            certificateStatus: certificateStatus,
            clients: [],
            data: {
                uuid: null,
                protocol: null,
                status: null,
                process_origin: null,
                process_uf: null,
                justice_branch: null,
                judging_body: null,
                final_judgment_date: null,
                calculation_update_date: null,
                debtors: [],
                creditors: [{ name: null, registration: null }],
                credits: [{ amount: null, currency: null, credit_origin: null, creditor_class: null, creditor_registration: null }],
                has_lawyer_fees: null,
                lawyers: [{ name: null, registration: null, oab: null, state: null, amount: null, fixed_at: null, document_file: null, document_file_url: null }],
                has_expert_fees: null,
                experts: [{ name: null, registration: null, amount: null, fixed_at: null, document_file: null, document_file_url: null }],
                chc_file: null,
                homologation_decision_file: null,
                calculation_spreadsheet_file: null,
                is_confidential: null,
                full_process_file: null,
                chc_file_url: null,
                homologation_decision_file_url: null,
                calculation_spreadsheet_file_url: null,
                full_process_file_url: null,
                observations: '',
                requested_documents: null,
            }
        }
    }),

    created() {
        if (this.$root.user) {
            this.user = this.$root.user;
            this.loadText();
            this.loadClients();
            this.loadRecords();
        }
    },

    computed: {
        pendingDocsRecords() {
            return (this.dataset.records || []).filter(r => this.hasPendingDocsRequest(r));
        },
        outdatedRecords() {
            return (this.dataset.records || []).filter(r => Number(r.status) === 6);
        },
        creditorRegistrationItems() {
            return this.dataset.data.creditors
                .map((creditor, index) => {
                    const registration = (creditor.registration || '').replace(/\D/g, '');
                    return {
                        text: `${creditor.name || `Credor ${index + 1}`} — ${this.formatRegistration(registration)}`,
                        value: registration
                    };
                })
                .filter(item => !!item.value);
        }
    },

    watch: {
        'dataset.data.creditors': {
            deep: true,
            handler() {
                this.ensureCreditAssociations();
            }
        }
    },

    methods: {
        requiredChoice(value) {
            return value !== null && value !== undefined || 'Selecione uma opção';
        },

        validateStep(step, showNotification = true) {
            if (step === 2) this.ensureCreditAssociations();
            const form = this.$refs.form;
            const section = this.$refs[`step${step}`];
            const inputs = form && Array.isArray(form.inputs) && section
                ? form.inputs.filter(input => input.$el && section.contains(input.$el))
                : [];
            let valid = true;

            inputs.forEach(input => {
                if (input.validate(true) === false) valid = false;
            });

            if (!valid && showNotification) {
                this.$dialog.notify.error('Preencha os campos obrigatórios desta etapa.', {
                    position: 'top-right',
                    timeout: 2000
                });
            }

            return valid;
        },

        nextStep() {
            if (!this.validateStep(this.dataset.formStep)) return;
            this.dataset.formStep += 1;
        },

        goToStep(step) {
            if (step === this.dataset.formStep) return;
            if (step < this.dataset.formStep) {
                this.dataset.formStep = step;
                return;
            }
            if (step === this.dataset.formStep + 1) this.nextStep();
        },

        validateAllSteps() {
            for (let step = 1; step <= 5; step += 1) {
                if (!this.validateStep(step, false)) {
                    this.dataset.formStep = step;
                    this.$dialog.notify.error('Revise os campos obrigatórios destacados antes de finalizar.', {
                        position: 'top-right',
                        timeout: 2500
                    });
                    return false;
                }
            }
            return true;
        },

        isCnpj(value) {
            const digits = (value || '').replace(/\D/g, '');
            return digits.length > 11;
        },

        formatRegistration(value) {
            const digits = (value || '').replace(/\D/g, '');
            if (digits.length === 11) {
                return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
            }
            if (digits.length === 14) {
                return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
            }
            return value || '';
        },

        openRegister() {
            eventHub.$emit("DIALOG-REGISTER", true);
        },

        openDoc(url) {
            if (!url) return;
            window.open(url, '_blank');
        },

        getStatusLabel(status) {
            const matched = this.dataset.certificateStatus.find(item => item.id === Number(status));
            return matched ? matched.name : 'Desconhecido';
        },

        getStatusColor(status) {
            const value = Number(status);
            if (value === 0) return 'warning';
            if (value === 1) return 'success';
            if (value === 2) return 'error';
            if (value === 4) return '#7E55AB';
            if (value === 5) return '#29B6F6';
            if (value === 6) return '#FB8C00';
            return 'gray';
        },

        getStatusDescription(status) {
            const descriptions = {
                0: 'Sua solicitação foi recebida e aguarda o início da análise.',
                1: 'Certidão aceita. Nenhuma ação necessária.',
                2: 'A solicitação não foi aprovada. Consulte os detalhes para entender o motivo.',
                3: 'A equipe está conferindo os dados e os documentos anexados.',
                4: 'A equipe pediu novos arquivos para concluir a análise.',
                5: 'Os novos documentos foram recebidos e aguardam conferência.',
                6: 'A certidão perdeu validade e precisa ser reenviada.'
            };
            return descriptions[Number(status)] || 'Consulte os detalhes e acompanhe as atualizações desta solicitação.';
        },

        getRecordDebtor(record) {
            const debtors = record.debtors || record.debtor_clients || [];
            if (!debtors.length) return '-';
            return debtors
                .map(item => item.name || (item.client && item.client.name) || item.client_name)
                .filter(Boolean)
                .join(', ') || '-';
        },

        getDocTypeLabel(type) {
            const value = Number(type);
            if (value === 1) return 'Certidão de Habilitação de Crédito';
            if (value === 2) return 'Processo integral (sigiloso)';
            return 'Documento';
        },

        parseRequestedDocs(value) {
            if (!value) return null;
            if (typeof value === 'string') {
                try { return JSON.parse(value); } catch (e) { return null; }
            }
            if (typeof value === 'object') return value;
            return null;
        },

        getRequestedDocs(source) {
            if (!source) return null;
            return this.parseRequestedDocs(source.requested_documents || source.requestedDocuments);
        },

        hasPendingFiles(requestedDocs) {
            const parsed = this.parseRequestedDocs(requestedDocs);
            if (!parsed || !Array.isArray(parsed.files)) return false;
            return parsed.files.some(f => Number(f.status) === 0);
        },

        hasPendingDocsRequest(record) {
            if (!record) return false;
            if (Number(record.status) === 4) return true;
            return this.hasPendingFiles(this.getRequestedDocs(record));
        },

        formatDate(date) {
            if (!date) return '-';
            return moment(date).format('DD/MM/YYYY HH:mm');
        },

        resetData() {
            this.dataset.data = {
                manifestation: null,
                uuid: null,
                protocol: null,
                status: null,
                process_origin: null,
                process_uf: null,
                justice_branch: null,
                judging_body: null,
                final_judgment_date: null,
                calculation_update_date: null,
                debtors: [],
                creditors: [{ name: null, registration: null }],
                credits: [{ amount: null, currency: null, credit_origin: null, creditor_class: null, creditor_registration: null }],
                has_lawyer_fees: null,
                lawyers: [{ name: null, registration: null, oab: null, state: null, amount: null, fixed_at: null, document_file: null, document_file_url: null }],
                has_expert_fees: null,
                experts: [{ name: null, registration: null, amount: null, fixed_at: null, document_file: null, document_file_url: null }],
                chc_file: null,
                homologation_decision_file: null,
                calculation_spreadsheet_file: null,
                is_confidential: null,
                full_process_file: null,
                chc_file_url: null,
                homologation_decision_file_url: null,
                calculation_spreadsheet_file_url: null,
                full_process_file_url: null,
                observations: '',
                requested_documents: null,
            };
        },

        async loadRecords() {
            this.dataset.loading = true;
            try {
                const result = await axios.get('api/users-creditors/credit-certificate-requests/lawyer', { });
                
                const content = result.data.content;
                
                this.dataset.records = (content && content.data) || content || [];
            } catch (error) {
                this.dataset.records = [];
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                });
            } finally {
                this.dataset.loading = false;
            }
        },

        viewRecord(record) {
            this.resetData();
            const d = this.dataset.data;

            const toDateInput = (v) => {
                if (!v) return null;
                const m = moment(v);
                return m.isValid() ? m.format('YYYY-MM-DD') : null;
            };

            d.uuid = record.uuid || null;
            d.protocol = record.protocol || null;
            d.status = record.status;
            d.process_origin = record.origin_process || record.process_origin || null;
            d.process_uf = record.process_uf || null;
            d.justice_branch = record.justice_branch || null;
            d.judging_body = record.judging_body || null;
            d.final_judgment_date = toDateInput(record.final_judgment_at || record.final_judgment_date);
            d.calculation_update_date = toDateInput(record.calculation_updated_at || record.calculation_update_date);
            d.observations = record.observations || '';
            d.is_confidential = record.is_confidential === true || record.is_confidential === 1;
            d.has_lawyer_fees = record.has_attorney_fees === true || record.has_attorney_fees === 1;
            d.has_expert_fees = record.has_expert_fees === true || record.has_expert_fees === 1;

            const debtors = record.debtors || record.debtor_clients || [];
            d.debtors = debtors.map(x => (typeof x === 'object' ? (x.client_id || x.id) : x));

            const creditors = record.creditors || [];
            d.creditors = creditors.length
                ? creditors.map(c => ({ name: c.name, registration: this.formatRegistration(c.registration) }))
                : [{ name: null, registration: null }];

            const credits = record.credits || [];
            d.credits = credits.length
                ? credits.map(c => ({
                    amount: c.amount,
                    currency: c.currency,
                    credit_origin: c.credit_origin || null,
                    creditor_class: c.class_type || c.creditor_class,
                    creditor_registration: ((c.registration || c.creditor_registration) || '').replace(/\D/g, '') || null
                }))
                : [{ amount: null, currency: null, credit_origin: null, creditor_class: null, creditor_registration: null }];

            const lawyers = record.attorneyFees || record.attorney_fees || record.lawyers || [];
            d.lawyers = lawyers.length
                ? lawyers.map(l => ({
                    name: l.name || null,
                    registration: this.formatRegistration(l.registration),
                    oab: l.oab,
                    state: l.uf || l.state,
                    amount: l.amount || null,
                    fixed_at: toDateInput(l.fixed_at),
                    document_file: null,
                    document_file_url: l.document_file_url || null
                }))
                : [{ name: null, registration: null, oab: null, state: null, amount: null, fixed_at: null, document_file: null, document_file_url: null }];

            const experts = record.expertFees || record.expert_fees || record.experts || [];
            d.experts = experts.length
                ? experts.map(e => ({
                    name: e.name || null,
                    registration: this.formatRegistration(e.registration),
                    amount: e.amount || null,
                    fixed_at: toDateInput(e.fixed_at),
                    document_file: null,
                    document_file_url: e.document_file_url || null
                }))
                : [{ name: null, registration: null, amount: null, fixed_at: null, document_file: null, document_file_url: null }];

            const chcDoc = record.chcUserDoc || record.chc_user_doc || null;
            const fullDoc = record.confidentialFullProcessUserDoc || record.confidential_full_process_user_doc || null;
            d.chc_file_url = record.chc_file_url || (chcDoc && (chcDoc.url || chcDoc.file_url)) || null;
            d.homologation_decision_file_url = record.homologation_decision_file_url || null;
            d.calculation_spreadsheet_file_url = record.calculation_spreadsheet_file_url || null;
            d.full_process_file_url = record.confidential_full_process_file_url || record.full_process_file_url || (fullDoc && (fullDoc.url || fullDoc.file_url)) || null;
            d.requested_documents = this.getRequestedDocs(record);
            d.manifestation = record.manifestation || null;

            this.dataset.currentRecordId = record.id || null;
            this.dataset.readonly = true;
            this.dataset.mode = 'form';
        },

        openUpdateDialog() {
            const reqDocs = this.parseRequestedDocs(this.dataset.data.requested_documents);
            if (!reqDocs || !Array.isArray(reqDocs.files)) return;
            this.dataset.requestedFiles = reqDocs.files.filter(f => Number(f.status) === 0);
            this.dataset.requestedDocsAt = reqDocs.requested_at;
            this.dataset.updateFiles = {};
            this.dataset.updateDialog = true;
            this.$nextTick(() => {
                if (this.$refs.updateForm) this.$refs.updateForm.resetValidation();
            });
        },

        openUpdateDialogFromRecord(record) {
            const reqDocs = this.getRequestedDocs(record);
            if (!reqDocs || !Array.isArray(reqDocs.files)) return;
            this.dataset.currentRecordId = record.id || null;
            this.dataset.requestedFiles = reqDocs.files.filter(f => Number(f.status) === 0);
            this.dataset.requestedDocsAt = reqDocs.requested_at;
            this.dataset.updateFiles = {};
            this.dataset.updateDialog = true;
            this.$nextTick(() => {
                if (this.$refs.updateForm) this.$refs.updateForm.resetValidation();
            });
        },

        openOutdatedUpdateDialog(record) {
            this.dataset.currentRecordId = record.id || null;
            this.dataset.requestedFiles = [{ type: 1, status: 0, updated_at: null }];
            this.dataset.requestedDocsAt = null;
            this.dataset.updateFiles = {};
            this.dataset.updateDialog = true;
            this.$nextTick(() => {
                if (this.$refs.updateForm) this.$refs.updateForm.resetValidation();
            });
        },

        onUpdateFileChange(type, file) {
            this.$set(this.dataset.updateFiles, type, file);
        },

        async submitDocumentUpdate() {
            if (this.$moduleBlocked('site.chc.request')) return;
            if (!this.$refs.updateForm.validate()) {
                this.$dialog.notify.error('Anexe todos os arquivos solicitados.', {
                    position: 'top-right',
                    timeout: 2000
                });
                return;
            }

            if (!this.dataset.currentRecordId) {
                this.$dialog.notify.error('Registro inválido.', {
                    position: 'top-right',
                    timeout: 2000
                });
                return;
            }

            this.dataset.loadingUpdate = true;
            try {
                const formData = new FormData();

                this.dataset.requestedFiles.forEach(file => {
                    const value = this.dataset.updateFiles[file.type];
                    if (!value) return;
                    if (Number(file.type) === 1) {
                        formData.append('chc_file', value);
                    } else if (Number(file.type) === 2) {
                        formData.append('confidential_full_process_file', value);
                    }
                });

                await axios.patch(
                    `/api/users-creditors/credit-certificate-requests/${this.dataset.currentRecordId}/update-documents`,
                    formData,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                );

                this.$dialog.notify.success('Documentos enviados com sucesso.', {
                    position: 'top-right',
                    timeout: 2000
                });

                this.dataset.updateDialog = false;
                this.dataset.updateFiles = {};
                this.dataset.requestedFiles = [];
                this.dataset.mode = 'list';
                this.dataset.readonly = false;
                this.resetData();
                await this.loadRecords();
                eventHub.$emit('CERTIFICATE-PENDING-DOCS-REFRESH');
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                });
            } finally {
                this.dataset.loadingUpdate = false;
            }
        },

        createNew() {
            this.resetData();
            this.dataset.completedProtocol = null;
            this.dataset.readonly = false;
            this.dataset.formStep = 1;
            this.dataset.mode = 'form';
        },

        backToList() {
            this.dataset.mode = 'list';
            this.dataset.completedProtocol = null;
            this.dataset.readonly = false;
            this.dataset.formStep = 1;
            this.resetData();
        },

        async loadText() {
            try {
                const result = await axios.get(`/api/dynamic-infos/keys`, {
                    params: { keys: 'certificate-claim-description' }
                });

                if (result.data.content && result.data.content.length > 0) {
                    this.dataset.description = result.data.content[0].value;
                } else {
                    this.dataset.description = '';
                }
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                });
            }
        },

        async loadClients() {
            try {
                const result = await axios.get(`/api/clients`, { params: { limit: 100000 } });
                this.dataset.clients = result.data.content.data || [];
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                });
            }
        },

        addCreditor() {
            this.dataset.data.creditors.push({ name: null, registration: null });
        },
        removeCreditor(index) {
            this.dataset.data.creditors.splice(index, 1);
            this.ensureCreditAssociations();
        },

        addCredit() {
            const registration = this.creditorRegistrationItems.length === 1
                ? this.creditorRegistrationItems[0].value
                : null;
            this.dataset.data.credits.push({
                amount: null,
                currency: null,
                credit_origin: null,
                creditor_class: null,
                creditor_registration: registration
            });
        },
        removeCredit(index) {
            this.dataset.data.credits.splice(index, 1);
        },

        addLawyer() {
            this.dataset.data.lawyers.push({ name: null, registration: null, oab: null, state: null, amount: null, fixed_at: null, document_file: null, document_file_url: null });
        },
        removeLawyer(index) {
            this.dataset.data.lawyers.splice(index, 1);
        },

        addExpert() {
            this.dataset.data.experts.push({ name: null, registration: null, amount: null, fixed_at: null, document_file: null, document_file_url: null });
        },
        removeExpert(index) {
            this.dataset.data.experts.splice(index, 1);
        },

        ensureCreditAssociations() {
            const registrations = this.creditorRegistrationItems.map(item => item.value);
            const onlyRegistration = registrations.length === 1 ? registrations[0] : null;

            this.dataset.data.credits.forEach(credit => {
                const current = (credit.creditor_registration || '').replace(/\D/g, '');
                if (onlyRegistration) {
                    credit.creditor_registration = onlyRegistration;
                } else if (!registrations.includes(current)) {
                    credit.creditor_registration = null;
                }
            });
        },

        onSubmitClick() {
            if (!this.validateAllSteps()) return;
            this.dataset.confirmDialog = true;
        },

        buildPayload() {
            const data = this.dataset.data;

            const creditors = data.creditors.map(c => ({
                name: c.name,
                registration: (c.registration || '').replace(/\D/g, '')
            }));

            const credits = data.credits.map(c => ({
                registration: (c.creditor_registration || data.creditors[0].registration || '').replace(/\D/g, ''),
                currency: c.currency,
                amount: c.amount,
                credit_origin: c.credit_origin,
                class_type: c.creditor_class
            }));

            const attorney_fees = data.has_lawyer_fees === true
                ? data.lawyers.map(l => ({
                    name: l.name,
                    registration: (l.registration || '').replace(/\D/g, ''),
                    oab: l.oab,
                    uf: l.state,
                    amount: l.amount,
                    fixed_at: l.fixed_at
                }))
                : [];

            const expert_fees = data.has_expert_fees === true
                ? data.experts.map(e => ({
                    name: e.name,
                    registration: (e.registration || '').replace(/\D/g, ''),
                    amount: e.amount,
                    fixed_at: e.fixed_at
                }))
                : [];

            return {
                origin_process: data.process_origin,
                process_uf: data.process_uf,
                justice_branch: data.justice_branch,
                judging_body: data.judging_body,
                final_judgment_at: data.final_judgment_date,
                calculation_updated_at: data.calculation_update_date,
                observations: data.observations,
                has_attorney_fees: data.has_lawyer_fees === true,
                has_expert_fees: data.has_expert_fees === true,
                is_confidential: data.is_confidential === true,
                confidential_warning_confirmed: true,
                debtor_client_ids: data.debtors,
                creditors,
                credits,
                attorney_fees,
                expert_fees,
                chc_file: data.chc_file,
                homologation_decision_file: data.homologation_decision_file,
                calculation_spreadsheet_file: data.calculation_spreadsheet_file,
                confidential_full_process_file: data.is_confidential === true ? data.full_process_file : null
            };
        },

        async confirmSubmit() {
            if (this.$moduleBlocked('site.chc.request')) return;
            this.dataset.confirmDialog = false;
            this.dataset.loadingSave = true;
            try {
                const data = this.dataset.data;
                const payload = this.buildPayload();
                const formData = new FormData();

                formData.append('origin_process', payload.origin_process);
                formData.append('process_uf', payload.process_uf);
                formData.append('justice_branch', payload.justice_branch);
                formData.append('judging_body', payload.judging_body);
                formData.append('final_judgment_at', payload.final_judgment_at);
                formData.append('calculation_updated_at', payload.calculation_updated_at);
                formData.append('observations', payload.observations || '');
                formData.append('has_attorney_fees', payload.has_attorney_fees ? 1 : 0);
                formData.append('has_expert_fees', payload.has_expert_fees ? 1 : 0);
                formData.append('is_confidential', payload.is_confidential ? 1 : 0);
                formData.append('confidential_warning_confirmed', payload.confidential_warning_confirmed ? 1 : 0);

                payload.debtor_client_ids.forEach(id => {
                    formData.append('debtor_client_ids[]', id);
                });

                formData.append('creditors', JSON.stringify(payload.creditors));
                formData.append('credits', JSON.stringify(payload.credits));
                formData.append('attorney_fees', JSON.stringify(payload.attorney_fees));
                formData.append('expert_fees', JSON.stringify(payload.expert_fees));

                if (payload.chc_file) {
                    formData.append('chc_file', payload.chc_file);
                }

                if (payload.homologation_decision_file) {
                    formData.append('homologation_decision_file', payload.homologation_decision_file);
                }

                if (payload.calculation_spreadsheet_file) {
                    formData.append('calculation_spreadsheet_file', payload.calculation_spreadsheet_file);
                }

                if (data.has_lawyer_fees === true) {
                    data.lawyers.forEach((lawyer, index) => {
                        if (lawyer.document_file) {
                            formData.append(`attorney_fee_document_${index}`, lawyer.document_file);
                        }
                    });
                }

                if (data.has_expert_fees === true) {
                    data.experts.forEach((expert, index) => {
                        if (expert.document_file) {
                            formData.append(`expert_fee_document_${index}`, expert.document_file);
                        }
                    });
                }

                if (payload.confidential_full_process_file) {
                    formData.append('confidential_full_process_file', payload.confidential_full_process_file);
                }

                const response = await axios.post('/api/users-creditors/credit-certificate-requests', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                const protocol = response.data && response.data.content
                    ? response.data.content.protocol
                    : null;

                this.$refs.form.reset();
                this.resetData();
                this.dataset.completedProtocol = protocol;
                this.dataset.readonly = false;
                await this.loadRecords();
                this.dataset.mode = 'success';
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), {
                    position: 'top-right',
                    timeout: 2000
                });
            } finally {
                this.dataset.loadingSave = false;
            }
        }
    }
};
</script>

<style lang="scss">
.chc-page {
    color: #171b2c;
    padding-bottom: 64px;

    .limiter-content {
        max-width: 1440px;
        margin: 0 auto;
    }
}

.chc-breadcrumb {
    color: #7b8296;
    font-size: 13px;

    span {
        margin: 0 8px;
        color: #aeb3c1;
    }
}

.chc-page-header,
.chc-section-header,
.chc-intro-card,
.chc-attention-card,
.chc-request-card {
    display: flex;
    align-items: center;
}

.chc-page-header {
    justify-content: space-between;
    gap: 24px;
    margin-bottom: 28px;
}

.chc-subtitle {
    color: #6f778c;
    font-size: 16px;
    line-height: 1.55;
    max-width: 760px;
}

.chc-primary-action {
    min-height: 44px;
    border-radius: 5px;
    letter-spacing: .04em;
    white-space: nowrap;
}

.chc-intro-card {
    justify-content: space-between;
    gap: 28px;
    padding: 24px 28px;
    border: 1px solid #dfe3ee;
    border-radius: 8px;
    background: #f8f9fd;
}

.chc-intro-copy {
    max-width: 820px;

    p {
        margin: 6px 0 0;
        color: #545c70;
        line-height: 1.55;
    }
}

.chc-eyebrow {
    color: var(--v-primary-base);
    font-size: 13px;
    font-weight: 700;
    letter-spacing: .06em;
    text-transform: uppercase;
}

.chc-requirements,
.chc-requests-section {
    margin-top: 32px;

    h2 {
        margin: 4px 0 18px;
        font-size: 22px;
    }
}

.chc-requirements-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
}

.chc-requirement {
    display: flex;
    gap: 14px;
    padding: 18px;
    border: 1px solid #e3e6ef;
    border-radius: 7px;
    background: #fff;

    > span {
        display: grid;
        place-items: center;
        flex: 0 0 32px;
        width: 32px;
        height: 32px;
        border: 1px solid var(--v-primary-base);
        border-radius: 50%;
        color: var(--v-primary-base);
        font-weight: 700;
    }

    strong {
        display: block;
        margin-bottom: 4px;
    }

    p {
        margin: 0;
        color: #737b8f;
        font-size: 13px;
        line-height: 1.45;
    }
}

.chc-attention-list {
    display: grid;
    gap: 12px;
    margin-top: 28px;
}

.chc-attention-card {
    gap: 16px;
    padding: 18px 20px;
    border: 1px solid #efcf96;
    border-left: 4px solid #c77a17;
    border-radius: 7px;
    background: #fffaf1;
}

.chc-attention-icon {
    display: grid;
    place-items: center;
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #fff0d4;
}

.chc-attention-content {
    flex: 1;

    p {
        margin: 3px 0 0;
        color: #6a604f;
        font-size: 13px;
    }
}

.chc-section-header {
    justify-content: space-between;
    gap: 20px;
    margin-bottom: 16px;
}

.chc-request-list {
    display: grid;
    gap: 12px;
}

.chc-request-card {
    gap: 24px;
    padding: 20px;
    border: 1px solid #e0e4ed;
    border-radius: 7px;
    background: #fff;
    box-shadow: 0 3px 12px rgba(25, 34, 67, .04);
}

.chc-request-main {
    flex: 1.15;
    min-width: 230px;

    h3 {
        margin: 10px 0 5px;
        font-size: 15px;
    }

    p {
        margin: 0;
        color: #747c90;
        font-size: 13px;
        line-height: 1.45;
    }
}

.chc-request-heading {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #41485a;
    font-size: 13px;
}

.chc-request-meta {
    display: grid;
    flex: 1;
    grid-template-columns: repeat(2, minmax(130px, 1fr));
    gap: 14px 20px;

    div {
        display: flex;
        flex-direction: column;
        gap: 3px;
    }

    span {
        color: #8a91a3;
        font-size: 11px;
        text-transform: uppercase;
    }

    strong {
        color: #343a4a;
        font-size: 12px;
        font-weight: 600;
    }
}

.chc-empty-state {
    margin-top: 28px;
    padding: 48px 24px;
    border: 1px dashed #cbd1df;
    border-radius: 8px;
    text-align: center;

    h2 {
        margin: 14px 0 5px;
        font-size: 20px;
    }

    p {
        margin: 0;
        color: #737b8f;
    }
}

.chc-success-card {
    max-width: 720px;
    margin: 48px auto 0;
    padding: 48px;
    border: 1px solid #dfe3ee;
    border-radius: 10px;
    background: #fff;
    box-shadow: 0 10px 30px rgba(34, 48, 112, .08);
    text-align: center;

    h2 {
        margin: 10px 0 8px;
        font-size: 28px;
    }

    > p {
        max-width: 540px;
        margin: 0 auto 26px;
        color: #6f778c;
        line-height: 1.6;
    }
}

.chc-success-icon {
    display: grid;
    place-items: center;
    width: 72px;
    height: 72px;
    margin: 0 auto 20px;
    border-radius: 50%;
    background: #35a853;
}

.chc-protocol-box {
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-width: 390px;
    margin: 0 auto 28px;
    padding: 18px 24px;
    border: 1px solid #ccd4ef;
    border-radius: 8px;
    background: #f6f8ff;

    span {
        color: #747c90;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: .05em;
        text-transform: uppercase;
    }

    strong {
        color: var(--v-primary-base);
        font-size: 24px;
        letter-spacing: .06em;
    }
}

.chc-stepper {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    padding: 18px 12px;
    border: 1px solid #dfe3ec;
    border-radius: 8px;
    background: #fff;

    button {
        position: relative;
        display: flex;
        align-items: center;
        gap: 9px;
        min-width: 0;
        padding: 0 10px;
        color: #81889b;
        text-align: left;

        &::after {
            position: absolute;
            z-index: 0;
            top: 17px;
            right: -12px;
            width: 24px;
            height: 1px;
            background: #d9dee8;
            content: "";
        }

        &:last-child::after {
            display: none;
        }

        span {
            position: relative;
            z-index: 1;
            display: grid;
            place-items: center;
            flex: 0 0 34px;
            width: 34px;
            height: 34px;
            border: 1px solid #cbd1dc;
            border-radius: 50%;
            background: #fff;
        }

        strong {
            overflow: hidden;
            font-size: 12px;
            text-overflow: ellipsis;
        }

        &.active,
        &.complete {
            color: var(--v-primary-base);
        }

        &.active span,
        &.complete span {
            border-color: var(--v-primary-base);
        }

        &.active span {
            background: var(--v-primary-base);
            color: #fff;
        }
    }
}

.chc-form-back,
.chc-form-flow {
    width: 100%;
    margin-right: auto;
    margin-left: auto;
}

.chc-form-section {
    padding: 30px;
    border: 1px solid #dfe3ec;
    border-radius: 10px;
    background: #f8f9fc;
}

.chc-form-section + .chc-form-section {
    margin-top: 18px;
}

.chc-form-section-heading {
    margin-bottom: 24px;

    h2 {
        margin: 0 0 5px;
        font-size: 21px;
    }

    p {
        margin: 0;
        color: #737b8f;
        font-size: 14px;
    }
}

.chc-form-step-label {
    display: block;
    margin-bottom: 5px;
    color: var(--v-primary-base);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .08em;
    text-transform: uppercase;
}

.chc-data-card {
    overflow: hidden;
    border: 1px solid #e1e5ee !important;
    border-radius: 9px !important;
    background: #fff !important;
}

.chc-data-card-title {
    min-height: 72px;
    padding: 15px 20px !important;
    border-bottom: 1px solid #e7eaf1;

    > div:first-child {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    > div:first-child > span {
        color: #252b3d;
        font-size: 18px;
        font-weight: 700;
    }

    small {
        color: #858c9e;
        font-size: 11px;
        font-weight: 500;
    }
}

.chc-data-card .v-card__text {
    padding: 20px !important;
}

.chc-add-link {
    min-width: auto !important;
    height: 26px !important;
    padding: 0 2px !important;
    font-size: 11px !important;
    font-weight: 500;
    letter-spacing: .02em !important;
    text-transform: none;

    .v-btn__content {
        color: var(--v-primary-base);
        font-size: 11px;
        font-weight: 500;
        opacity: .82;
    }
}

.chc-entry {
    position: relative;
}

.chc-entry + .chc-entry {
    margin-top: 16px;
    padding-top: 20px;
    border-top: 1px solid #e7eaf1;
}

.chc-entry-label {
    display: inline-block;
    margin-bottom: 8px;
    color: #596177;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: .04em;
    text-transform: uppercase;
}

.chc-credit-owner {
    margin-bottom: 10px;
    padding: 12px 12px 5px !important;
    border-left: 3px solid rgba(47, 64, 144, .55);
    border-radius: 5px;
    background: #f6f8ff;

    .v-input {
        max-width: 680px;
    }
}

.chc-entry-remove {
    position: absolute !important;
    top: 2px;
    right: -8px;
}

.chc-entry-label ~ .row {
    padding-right: 40px;
}

.chc-form-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 0;
}

.title-page {
    color: var(--v-primary-base);
    font-family: "Raleway";
    font-size: 40px;
    font-style: normal;
    font-weight: 700;
    line-height: 38px;
}

.button-with-border {
  background-color: #ffffff;
  border: 2px solid #FF6F00;
  padding: 10px 20px;
  border-radius: 5px;
}

.button-with-border:hover {
  background-color: #f7994d;
  color: rgb(247, 243, 243) !important;
}

@media (max-width: 960px) {
    .chc-page-header,
    .chc-intro-card,
    .chc-request-card {
        align-items: stretch;
        flex-direction: column;
    }

    .chc-request-meta {
        width: 100%;
    }

    .chc-stepper {
        grid-template-columns: repeat(5, 1fr);

        button {
            justify-content: center;

            strong {
                display: none;
            }
        }
    }
}

@media (max-width: 600px) {
    .chc-success-card {
        margin-top: 28px;
        padding: 32px 20px;
    }

    .title-page {
        font-size: 29px;
        line-height: 1.2;
    }

    .chc-requirements-grid,
    .chc-request-meta {
        grid-template-columns: 1fr;
    }

    .chc-attention-card,
    .chc-section-header {
        align-items: stretch;
        flex-direction: column;
    }

    .chc-form-section {
        padding: 18px 14px;
    }

    .chc-data-card-title {
        align-items: flex-start;
        flex-direction: column;
        gap: 12px;

        .v-spacer {
            display: none;
        }
    }

    .chc-data-card-title .v-btn {
        width: 100%;
    }

    .chc-data-card-title .chc-add-link {
        width: auto !important;
    }
}

</style>
